import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { dailyTasksApi } from '@/api/dailyTasks';
import { taskLinkFor } from '@/lib/taskLinks';
import { cn } from '@/lib/utils';
import { useAuthStore } from '@/stores/auth';

import { WordOfTheDayCard } from '../WordOfTheDayCard';

/**
 * Правая колонка расписания (макет «Desktop Web v2»): счёт по неделе и
 * разговорные комнаты.
 *
 * ⚠️ КАРТОЧКИ «ЗАПИСИ ЗАНЯТИЙ» ИЗ МАКЕТА ЗДЕСЬ НЕТ. Список записей отдаётся
 * только админке (`/admin/recordings`); ученику такого запроса нет вовсе.
 * Нарисовать карточку с чужими данными или с пустышкой — хуже, чем не рисовать:
 * ученик решит, что записи есть, и будет их искать. Появится запрос — вернём.
 */
export function ScheduleSidebar({ weekTotal, past }: { weekTotal: number; past: number }) {
  const { t } = useTranslation();
  const user = useAuthStore((st) => st.user);

  const tasks = useQuery({
    queryKey: ['student', 'daily-tasks', 'schedule'],
    queryFn: () => dailyTasksApi.today(),
  });

  return (
    <div>
      <div className="rounded-[22px] bg-surface-container px-5 py-[18px]">
        <h3 className="mb-[13px] text-[13.5px] font-bold text-on-surface">{t('schedule.thisWeek')}</h3>
        <div className="flex justify-between">
          {[
            { v: weekTotal, l: t('schedule.weekTotal'), c: 'text-on-surface' },
            { v: past, l: t('schedule.weekPast'), c: 'text-info' },
            { v: Math.max(weekTotal - past, 0), l: t('schedule.weekAhead'), c: 'text-on-primary-container' },
          ].map((x) => (
            <div key={x.l}>
              <div className={cn('font-display text-[20px] font-bold tabular-nums', x.c)}>{x.v}</div>
              <div className="text-[11px] text-on-surface-variant">{x.l}</div>
            </div>
          ))}
        </div>
      </div>

      {/* ⚠️ КАРТОЧКИ РАЗГОВОРНЫХ КОМНАТ ЗДЕСЬ БОЛЬШЕ НЕТ — РЕШЕНИЕ ЗАКАЗЧИКА
          07.09.2026: «ученики не могут их открывать». Комнату заводит куратор;
          приглашение «Открыть» вело ученика туда, где ему нечего делать, и
          показывало «0 комнат открыто» — то есть звало в пустоту.

          Вместо неё то, чем ученик может заняться прямо сейчас: слово дня и
          задания на сегодня. */}
      <div className="mt-3.5">
        <WordOfTheDayCard tone="info" />
      </div>

      {tasks.data && tasks.data.length > 0 && (
        <div className="mt-3.5">
          <div className="mx-0.5 mb-2 flex items-baseline gap-3">
            <h3 className="flex-1 font-display text-[14.5px] font-bold text-on-surface">
              {t('learn.todayTasks')}
            </h3>
            <span className="text-[12.5px] font-semibold tabular-nums text-on-surface-variant">
              {tasks.data.filter((x) => x.done).length}/{tasks.data.length}
            </span>
          </div>
          <div className="flex flex-col gap-2">
            {tasks.data.slice(0, 4).map((task) => {
              // В приложении — только туда, куда ученик попадёт (`lib/taskLinks.ts`).
              const to = taskLinkFor(task, user);
              const row = (
                <span className="flex items-center gap-3 rounded-[20px] bg-surface-container px-[18px] py-3.5">
                  {task.done ? (
                    <span className="material-symbols-outlined flex-none text-[19px] text-primary">
                      check_circle
                    </span>
                  ) : (
                    <span className="h-[18px] w-[18px] flex-none rounded-full border-2 border-info" />
                  )}
                  <span
                    className={cn(
                      'min-w-0 flex-1 truncate text-[13px]',
                      task.done ? 'text-on-surface-variant line-through' : 'font-semibold text-on-surface',
                    )}
                  >
                    {task.title}
                  </span>
                  {to && (
                    <span className="material-symbols-outlined flex-none text-[16px] text-on-surface-variant">
                      chevron_right
                    </span>
                  )}
                </span>
              );
              return to ? (
                <Link key={task.id} to={to} className="hover:opacity-90">
                  {row}
                </Link>
              ) : (
                <span key={task.id}>{row}</span>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
